import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  accessTokenLocalStorageKey,
  initialWalletLoginLocalStorageKey,
  userIdLocalStorageKey,
} from '@mprolab/sdk'
import getSignature, { type SignableWallet } from 'utils/getSignature'
import { authApi } from 'api/axios'
import getApiHeaders from 'utils/getApiHeaders'

type WalletAuthPayload = {
  accessToken: string
  userId: string
  isUserVerified: boolean
  account: string
}

type WalletLoginResponse = {
  account?: { userId?: string }
  token?: { accessToken?: string }
  initial?: boolean
}

const persistWalletSession = ({
  accessToken,
  userId,
}: {
  accessToken: string
  userId: string
}) => {
  localStorage.setItem(accessTokenLocalStorageKey, accessToken)
  localStorage.setItem(userIdLocalStorageKey, userId)
  localStorage.setItem(initialWalletLoginLocalStorageKey, 'true')
}

export const getUserAuthData = createAsyncThunk<
  WalletAuthPayload,
  { account?: string; provider?: SignableWallet }
>('user/getUserAuthData', async ({ account, provider }) => {
  if (!account || !provider) {
    throw new Error('Wallet account or provider missing')
  }

  const { walletAddress, signature, hash } = await getSignature(account, provider)
  const response = await authApi.post<WalletLoginResponse>(
    'v2/login',
    {
      wallet: walletAddress,
      signature,
    },
    {
      headers: getApiHeaders({ account: walletAddress, hash }),
    },
  )

  const accessToken = response.data?.token?.accessToken?.trim() ?? ''
  const userId = response.data?.account?.userId?.trim() ?? ''

  if (!accessToken || !userId) {
    throw new Error('Wallet auth succeeded but token payload is incomplete')
  }

  persistWalletSession({ accessToken, userId })

  return {
    accessToken,
    userId,
    isUserVerified: true,
    account: walletAddress,
  }
})

export const createAccount = createAsyncThunk<
  WalletAuthPayload,
  {
    account?: string
    provider?: SignableWallet
    personalDetails?: { username?: string; country?: string; avatar?: string }
    query?: object
    rulesCheckbox?: { checked: boolean; updatedAt: string }
    merge?: boolean
    rulesCheckboxChecked?: boolean
  }
>('user/createAccount', async ({ account, provider }) => {
  if (!account || !provider) {
    throw new Error('Wallet account or provider missing')
  }

  const { walletAddress, signature, hash } = await getSignature(account, provider)
  const response = await authApi.post<WalletLoginResponse>(
    'create-account',
    {
      wallet: walletAddress,
      signature,
    },
    {
      headers: getApiHeaders({ account: walletAddress, hash }),
    },
  )

  const accessToken = response.data?.token?.accessToken?.trim() ?? ''
  const userId = response.data?.account?.userId?.trim() ?? ''

  if (!accessToken || !userId) {
    throw new Error('Wallet account creation succeeded but token payload is incomplete')
  }

  persistWalletSession({ accessToken, userId })

  return {
    accessToken,
    userId,
    isUserVerified: true,
    account: walletAddress,
  }
})

export const clearUserData = createAction('user/clearUserData')

export const setLoadingUserData = createAction<boolean>('user/setLoadingUserData')

export const setAwaitingPath = createAction<string>('user/setAwaitingPath')
